import { ClientAuthenticatedRoutes } from "@edgetier/client-components";
import * as Sentry from "@sentry/react";
import createAuthRefreshInterceptor from "axios-auth-refresh";
import Navigation from "components-for/application/navigation";
import MobileNavigation from "components-for/navigation/mobile-navigation";
import toast from "react-hot-toast";
import { axios, createAuthorisationHeader } from "utilities/axios";
import { deleteAccessToken, getAccessToken } from "utilities/access-token";
import { FunctionComponent, memo, useCallback, useEffect, useMemo, useState } from "react";
import { IClient } from "@edgetier/watchtower-types";
import { initialiseTracking } from "utilities/track-user";
import { Outlet, useNavigate } from "react-router-dom";
import { SpinnerUntil } from "@edgetier/components";
import { IProps } from "./authenticated-routes.types";
import ReactHeap from "heap";
import { getLoginRedirectUrl } from "utilities-for/authentication/get-login-redirect-url";
import { useUser } from "hooks-for/users/use-user";
import { setClientId } from "utilities/client-id";
import { refreshToken } from "utilities/refresh-token";
import { useClient } from "hooks-for/clients/use-client";
import { useMediaCheck } from "hooks-for/responsive-ui/use-media-check";
import { ClientContext } from "context-for/client/client-context";
import { useFlagsUtilities } from "@edgetier/feature-flags";
import chameleon from "@chamaeleonidae/chmln";
import { addVelarisWidget } from "utilities/add-velaris-widget";
import "./authenticated-routes.scss";

// TODO: Authentication failure not working. User isn't signed out.

const AuthenticatedRoutes: FunctionComponent<IProps> = () => {
    const [isUserInitialized, setIsUserInitialized] = useState(false);
    const [isClientInitialized, setIsClientInitialized] = useState(false);

    const FlagsUtils = useFlagsUtilities();
    const navigate = useNavigate();
    const accessToken = useMemo(() => getAccessToken(), []);
    const [hasAccessToken, setHasAccessToken] = useState(typeof accessToken === "string");

    /**
     * If there's an authentication problem, delete any access token and redirect the user to the login screen.
     */
    const onAuthenticationFailure = useCallback(() => {
        deleteAccessToken();
        Sentry.getCurrentScope().setUser(null);
        navigate(getLoginRedirectUrl(`${window.location.pathname}${window.location.search}`));
    }, [navigate]);

    // Request the user's details once they have an access token.
    const { data: user } = useUser({
        enabled: hasAccessToken,
        onError: onAuthenticationFailure,
    });

    // Request the user's client.
    const { data: client, isLoading } = useClient(user?.clientId!, {
        enabled: typeof user !== "undefined",
        onError: onAuthenticationFailure,
    });

    useEffect(() => {
        if (user && !isUserInitialized) {
            setIsUserInitialized(true);
            Sentry.setUser({ userId: user.userId });
            try {
                if (document.getElementById("velaris_survey") === null) {
                    addVelarisWidget(
                        user.clientId,
                        user.userId,
                        `${user.firstName} ${user.surname}`,
                        user.emailAddress
                    );
                }
            } catch {
                // Continue execution if the widget fails to load.
            }

            try {
                if (process.env.NODE_ENV !== "development" && !(FlagsUtils as any)?.evaluationContext?.identity) {
                    // TODO: Add the client name when the client is loaded.
                    FlagsUtils.identify(`${user.userId}_${user.firstName}_${user.surname}`, {
                        name: `${user.firstName} ${user.surname}`,
                        clientId: user.clientId,
                        emailAddress: user.emailAddress,
                        roleId: user.roleId,
                        role: user.role,
                        timezoneId: user.timeZoneId,
                    });
                }
            } catch {
                // Continue execution if the feature flags identity fails.
            }

            setClientId(user.clientId);
            // Generate a new access token when the current one expires.
            createAuthRefreshInterceptor(axios, async (request) => {
                try {
                    await refreshToken();
                    request.response.config.headers["Authorization"] = createAuthorisationHeader();
                    return Promise.resolve();
                } catch {
                    toast.error("Authentication failed.");
                    onAuthenticationFailure();
                }
            });
        }
    }, [isUserInitialized, user, FlagsUtils, onAuthenticationFailure]);

    useEffect(() => {
        if (!isClientInitialized && user && client) {
            setIsClientInitialized(true);
            const name = `${user?.firstName} ${user?.surname}`;
            initialiseTracking(user?.userId!, name, user?.emailAddress!, client);
            if (
                process.env.NODE_ENV !== "development" &&
                process.env.NODE_ENV !== "test" &&
                typeof user !== "undefined"
            ) {
                ReactHeap.identifyUser(user.emailAddress, {
                    clientName: client.client,
                    name,
                    emailAddress: user.emailAddress,
                });

                // Chameleon is used to show announcements, feature tours etc. We need to identify users in Chameleon
                // because feature tours need to be conditionally displayed depending on who we have released them to.
                chameleon.identify(user.userId, {
                    name,
                    role: user.role,
                    company: {
                        uid: client.clientId,
                        name: client.client,
                    },
                });
            }
            if (process.env.NODE_ENV !== "development" && FlagsUtils.getTrait("client") !== client.client) {
                // TODO: Add the client name when the client is loaded.
                FlagsUtils.setTrait("client", client.client);
            }
        }
    }, [isClientInitialized, user, client, FlagsUtils]);

    // On load, check if the user is signed in, and attempt to authenticate if not.
    useEffect(() => {
        const authenticate = async () => {
            try {
                if (typeof accessToken !== "string") {
                    await refreshToken();
                    setHasAccessToken(true);
                }
            } catch {
                onAuthenticationFailure();
            }
        };

        authenticate();
    }, [accessToken, onAuthenticationFailure]);

    const { isMobile, isTablet } = useMediaCheck();

    return (
        <SpinnerUntil<IClient> data={client} isReady={!isLoading}>
            {client && (
                <ClientContext.Provider value={client}>
                    <ClientAuthenticatedRoutes>
                        {isMobile || isTablet ? (
                            <div className="authenticated-routes__mobile-navigation">
                                <MobileNavigation />
                            </div>
                        ) : (
                            <Navigation />
                        )}
                        <Outlet />
                    </ClientAuthenticatedRoutes>
                </ClientContext.Provider>
            )}
        </SpinnerUntil>
    );
};

export default memo(AuthenticatedRoutes);
